// Enhanced WidgetList component with working search functionality
import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ENHANCED_WIDGET_CATEGORIES, ALL_ENHANCED_WIDGETS } from './EnhancedWidgets';

function DraggableWidget({ widget, onElementSelect }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'widget',
    item: { widget },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <Paper
      ref={drag}
      data-widget-sidebar // Add this data attribute for reference
      elevation={1}
      sx={{
        p: 2,
        mb: 1,
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
          transform: 'translateY(-1px)',
        },
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: '1.5rem', mr: 1 }}
          role="img"
          aria-label={widget.label}
        >
          {widget.icon}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {widget.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {widget.description}
          </Typography>
        </Box>
      </Box>
      <Chip
        label={widget.category}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ fontSize: '0.6rem', height: '20px' }}
      />
    </Paper>
  );
}

function EnhancedWidgetList({ searchQuery: externalSearchQuery, onElementSelect, onSearchChange }) {
  const [selectedCategory, setSelectedCategory] = useState('basic');

  const categoryOptions = [
    { value: 'all', label: 'All Widgets' },
    { value: 'basic', label: 'Basic Inputs' },
    { value: 'enhanced', label: 'Enhanced Inputs' },
    { value: 'advanced', label: 'Advanced Components' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'selection', label: 'Selection Controls' },
    { value: 'layout', label: 'Layout Components' },
  ];

  const getWidgetsForCategory = (category) => {
    let widgets;
    if (category === 'all') {
      widgets = ALL_ENHANCED_WIDGETS;
    } else {
      widgets = ENHANCED_WIDGET_CATEGORIES[category] || [];
    }

    // Apply search filter using external search query
    if (externalSearchQuery && externalSearchQuery.trim()) {
      const searchTerm = externalSearchQuery.toLowerCase().trim();
      widgets = widgets.filter((widget) => widget.label.toLowerCase().includes(searchTerm)
        || widget.description.toLowerCase().includes(searchTerm)
        || widget.type.toLowerCase().includes(searchTerm));
    }

    return widgets;
  };

  const widgets = getWidgetsForCategory(selectedCategory);

  return (
    <Box sx={{
      p: 2, height: '100%', display: 'flex', flexDirection: 'column',
    }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Widget Library
      </Typography>

      {/* Search Input */}
      <TextField
        placeholder="Search widgets..."
        value={externalSearchQuery || ''}
        onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Category Selector */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          label="Category"
          size="small"
        >
          {categoryOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Widget Count */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
        {widgets.length}
        {' '}
        widget
        {widgets.length !== 1 ? 's' : ''}
        {' '}
        available
        {externalSearchQuery && ` for "${externalSearchQuery}"`}
      </Typography>

      {/* Widgets Grid */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {widgets.length > 0 ? (
          <Grid container spacing={1}>
            {widgets.map((widget) => (
              <Grid item xs={12} key={widget.id}>
                <Tooltip title={widget.description} placement="right">
                  <div>
                    <DraggableWidget widget={widget} onElementSelect={onElementSelect} />
                  </div>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              color: 'text.secondary',
            }}
          >
            <Typography variant="h6">No widgets found</Typography>
            <Typography variant="body2">
              {externalSearchQuery
                ? 'Try a different search term or category'
                : 'Try selecting a different category'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default EnhancedWidgetList;
